<template>
    <div>
        <div 
            class="emailSettingPreferencesEmailsInput__row"
            :class="{ 'emailSettingPreferencesEmailsInput__row--editing': isEditing }"
        >
            <div
                v-if="isEditing || emails.length > 0"
                class="emailSettingPreferencesEmailsInput__field"
                :class="{
                    'emailSettingPreferencesEmailsInput__field--error': hasError,
                    'emailSettingPreferencesEmailsInput__field--editing': isEditing
                }"
                @click="focusInput"
            >
                <div class="emailSettingPreferencesEmailsInput__tags">
                    <CompassTagInteractive
                        v-for="(email, index) in emails"
                        :key="email.value"
                        :size="CompassEnums.TagInteractiveSize.SMALL"
                        :color="email.isValid && !isExceeding(index) ? CompassEnums.TagInteractiveColorType.STROKED : CompassEnums.TagInteractiveColorType.RED"
                        :modelValue="email.value"
                        :valueLabel="email.value"
                        @tag-remove="removeEmail(email.value)"
                    />
                    <input
                        v-if="isEditing"
                        ref="inputRef"
                        v-model="inputValue"
                        type="text"
                        class="emailSettingPreferencesEmailsInput__input saas-custom-input"
                        :placeholder="emails.length === 0 ? $t.__('email.EmailSettingPreferences.emailsAddedToCC.placeholder') : ''"
                        @keydown.enter.prevent="addEmail"
                        @keydown.,.prevent="addEmail"
                        @keydown.;.prevent="addEmail"
                        @paste="handlePaste"
                    />
                    <CompassButton
                        v-if="!isEditing"
                        class="emailSettingPreferencesEmailsInput__editButton"
                        :label="$t.__('email.EmailSettingPreferences.emailsAddedToCC.editEmailsButton')"
                        leftIcon="edit"
                        :size="CompassEnums.ButtonSize.SMALL"
                        :type="CompassEnums.ButtonType.TERTIARY"
                        @click="enterEditMode"
                    />
                </div>
            </div>

            <div v-if="isEditing" class="emailSettingPreferencesEmailsInput__actions">
                <CompassButton
                    :label="$t.__('common.actions.save')"
                    :size="CompassEnums.ButtonSize.MEDIUM"
                    :disabled="!canSave"
                    @click="save"
                />
                <CompassIconButton
                    icon="close"
                    :size="CompassEnums.IconButtonSize.MEDIUM"
                    @click="cancel"
                />
            </div>

            <CompassButton
                v-if="!isEditing && emails.length === 0"
                :label="$t.__('email.EmailSettingPreferences.emailsAddedToCC.addEmailButton')"
                leftIcon="plus"
                :size="CompassEnums.ButtonSize.SMALL"
                :type="CompassEnums.ButtonType.TERTIARY"
                @click="enterEditMode"
            />
        </div>
        <p
            v-if="isEditing"
            class="emailSettingPreferencesEmailsInput__counter"
            :class="{ 'emailSettingPreferencesEmailsInput__counter--exceeded': isLimitExceeded }"
        >
            {{ counterMessage }}
        </p>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, nextTick, watch } from 'vue';
import { CompassEnums } from '@sellsy/compass2';
import { Translator } from 'js/shims-vue';
import { validateEmail } from 'Helpers/validator/EmailValidator';

const MAX_EMAILS = 20;

interface Email {
    value: string;
    isValid: boolean;
}

const modelValue = defineModel<string[]>({ default: () => [] });

const $t = inject('$t') as Translator;

const emails = ref<Email[]>([]);
const inputValue = ref('');
const inputRef = ref<HTMLInputElement | null>(null);
const isEditing = ref(false);

const isValidEmail = (email: string): boolean => {
    return validateEmail(email.trim());
};

const toEmailObjects = (values: string[]): Email[] => {
    return values.map((value) => ({ value, isValid: isValidEmail(value) }));
};

const getValidEmailValues = (): string[] => {
    return emails.value.filter((email) => email.isValid).map((email) => email.value);
};

const hasChanged = (newEmails: string[]): boolean => {
    if (newEmails.length !== modelValue.value.length) {
        return true;
    }
    const sortedNew = [...newEmails].sort();
    const sortedOriginal = [...modelValue.value].sort();
    return sortedNew.some((email, index) => email !== sortedOriginal[index]);
};

const hasError = computed(() => emails.value.some((email) => !email.isValid));

const hasInvalidInput = computed(() => {
    const trimmed = inputValue.value.trim();
    return trimmed !== '' && !isValidEmail(trimmed);
});

const isLimitExceeded = computed(() => emails.value.length > MAX_EMAILS);

const isExceeding = (index: number): boolean => {
    return index >= MAX_EMAILS;
};

const counterMessage = computed(() => {
    const params = { count: String(emails.value.length), max: String(MAX_EMAILS) };
    if (isLimitExceeded.value) {
        return $t.__('email.EmailSettingPreferences.emailsAddedToCC.emailCountExceeded', params);
    }
    return $t.n__(
        'email.EmailSettingPreferences.emailsAddedToCC.emailCount',
        null,
        emails.value.length,
        params,
    );
});

const canSave = computed(() => {
    return !hasError.value && !hasInvalidInput.value && !isLimitExceeded.value;
});

watch(
    () => modelValue.value,
    (newValue) => {
        if (!isEditing.value) {
            emails.value = toEmailObjects(newValue);
        }
    },
    { immediate: true },
);

const addEmailToList = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
        return;
    }
    
    // Check if email already exists
    if (emails.value.some((email) => email.value.toLowerCase() === trimmedValue.toLowerCase())) {
        return;
    }
    
    emails.value.push({
        value: trimmedValue,
        isValid: isValidEmail(trimmedValue),
    });
};

const addEmail = () => {
    if (inputValue.value.trim()) {
        addEmailToList(inputValue.value);
        inputValue.value = '';
    }
};

const handlePaste = (event: ClipboardEvent) => {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const separatorRegex = /[,;\s]+/;
    const emailsList = pastedText.split(separatorRegex).filter((email) => email.trim());
    
    for (const email of emailsList) {
        addEmailToList(email);
    }
    inputValue.value = '';
};

const removeEmail = (emailValue: string) => {
    emails.value = emails.value.filter((email) => email.value !== emailValue);
    
    if (!isEditing.value) {
        modelValue.value = getValidEmailValues();
    }
};

const focusInput = () => {
    if (!isEditing.value) {
        enterEditMode();
    } else {
        inputRef.value?.focus();
    }
};

const enterEditMode = () => {
    isEditing.value = true;
    nextTick(() => inputRef.value?.focus());
};

const save = () => {
    addEmail();
    isEditing.value = false;
    const validEmails = getValidEmailValues();
    if (hasChanged(validEmails)) {
        modelValue.value = validEmails;
    }
};

const cancel = () => {
    emails.value = toEmailObjects(modelValue.value);
    inputValue.value = '';
    isEditing.value = false;
};
</script>

<style lang="scss" scoped>
.emailSettingPreferencesEmailsInput {
    &__row {
        display: flex;
        align-items: start;
        gap: var(--spacing-0);
        width: 100%;

        &--editing {
            gap: var(--spacing-1);
        }
    }

    &__field {
        flex: 1;
        border-radius: 4px;
        cursor: text;

        &--editing {
            padding: var(--spacing-0);
            border: 1px solid var(--colors-common-border-default);

            &:focus-within {
                border-color: var(--colors-common-border-focus);
                box-shadow: 0 0 0 2px var(--colors-common-focus-ring);
            }
        }

        &--error {
            border: 1px solid var(--colors-common-border-alert);
        }
    }

    &__tags {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacing-0);
        align-items: center;
    }

    &__editButton {
        margin-left: calc(var(--spacing-1) - var(--spacing-0));
    }

    &__input {
        flex: 1;
        min-width: 150px;
        height: 24px;
        padding: 0;
        border: 0;
        background: transparent;
        outline: none;

        &::placeholder {
            color: var(--colors-common-text-subtle-default);
        }
    }

    &__actions {
        display: flex;
        align-items: center;
        gap: var(--spacing-1);
        flex-shrink: 0;
    }

    &__counter {
        margin: var(--spacing-0) 0 0 0;
        font-size: var(--font-size-12);
        line-height: var(--line-heights-16);
        color: var(--colors-common-text-subtle-default);

        &--exceeded {
            color: var(--colors-common-text-alert-default);
        }
    }

}

</style>